<template>
	<div
		class="rating"
		:class="[{ loading }, size, { hovering }, { interactive }]"
	>
		<div
			class="star-container"
			@mouseleave="handleMouseLeave"
		>
			<div class="stars empty">
				<template v-if="$rating === 0">
					<span
						v-for="i in max"
						@mouseover="handleMouseOver(i)"
						@click="handleClick(i)"
						>&#9734;</span
					>
				</template>
				<template v-else>
					<span
						v-for="i in max"
						@mouseover="handleMouseOver(i)"
						@click="handleClick(i)"
						>&#9733;</span
					>
				</template>
			</div>
			<div
				class="stars full"
				:style="{ width: ratingPercentage }"
			>
				<span
					v-for="i in max"
					@mouseover="handleMouseOver(i)"
					@click="handleClick(i)"
					>&#9733;</span
				>
			</div>
		</div>
		<component
			is="router-link"
			v-if="label && to"
			class="label"
			>{{ $rating }}
			<template v-if="count">({{ count }})</template>
		</component>
		<a
			:href="href"
			v-else-if="label && href"
			class="label"
			>{{ $rating }}
			<template v-if="count">({{ count }})</template>
		</a>
		<span
			v-else-if="label"
			class="label"
			>{{ $rating }} <template v-if="count">({{ count }})</template></span
		>
	</div>
</template>

<script>
/**
 * Used to show ratings of products and services.
 * @displayName TnRating
 */
export default defineComponent({
	name: "TnRating",

	props: {
		/**
		 * Average rating
		 */
		rating: {
			type: Number,
		},
		/**
		 * Total number of reviews
		 */
		count: {
			type: Number,
		},
		/**
		 * Max number of stars
		 */
		max: {
			type: Number,
			default: 5,
		},
		/**
		 * Reserve space for the component and shows a loading animation
		 */
		loading: {
			type: Boolean,
			default: false,
		},
		/**
		 * Marginal change of size and spacing between the stars
		 */
		size: {
			type: String,
			validator: (input) => ["s", "m", "l", "xl"].includes(input),
			default: "s",
		},
		/**
		 * Show the average rating as text and total number of reviews (if provided)
		 */
		label: {
			type: Boolean,
			default: false,
		},
		href: {
			type: String,
		},
		to: {
			type: String,
		},
		interactive: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			hovering: 0,
		};
	},

	computed: {
		ratingPercentage() {
			return `${(this.$rating / this.max) * 100}%`;
		},
		$rating() {
			return this.hovering || this.rating || 0;
		},
	},

	methods: {
		handleMouseOver(index) {
			if (this.interactive) {
				this.hovering = index;
			}
		},
		handleClick(index) {
			if (this.interactive) {
				this.$emit("update:modelValue", index);
			}
		},
		handleMouseLeave() {
			if (this.interactive) {
				this.hovering = 0;
			}
		},
	},
});
</script>

<style lang="scss" scoped>
@use "@/assets/scss/variables" as variables;

.rating {
	display: flex;
	align-items: center;

	a {
		color: variables.$color-cta-default;
		text-decoration: none;
	}

	&.interactive {
		.star-container {
			.stars {
				cursor: pointer;
			}
		}
	}

	.star-container {
		display: inline-block;
		width: auto;
		position: relative;

		.stars {
			overflow: hidden;
			top: 0;
			font-size: 12px;
			line-height: 12px;

			&.empty {
				color: variables.$color-neutrals-200-tint;
			}

			&.full {
				color: variables.$color-primary-telenor;
				z-index: 1;
				position: absolute;
			}
		}
	}

	&.m {
		.stars {
			font-size: 14px;
			line-height: 14px;
			letter-spacing: 3px;
		}

		&.loading {
			height: 14px;
			min-width: 85px;
		}
	}

	&.l {
		.stars {
			font-size: 16px;
			line-height: 16px;
			letter-spacing: 1px;
		}

		&.loading {
			height: 16px;
			min-width: 85px;
		}
	}

	&.xl {
		.stars {
			font-size: 24px;
			line-height: 24px;
			letter-spacing: 1px;
		}

		&.loading {
			height: 24px;
			min-width: 85px;
		}

		.label {
			font-size: 16px;
			line-height: 16px;
			margin-left: variables.$spacing-s;
			font-family: variables.$font-family-telenor-base;
			position: relative;
			top: 2px;
		}
	}

	&.loading {
		position: relative;
		overflow: hidden;
		opacity: 0.7;
		height: 12px;
		min-width: 70px;

		&::after {
			content: "";
			display: block;
			position: absolute;
			top: 0;
			bottom: 0;
			width: 100%;
			height: 100%;
			border-radius: 5px;
			animation: 0.7s skeleton-loading linear infinite alternate;
		}
	}

	.label {
		font-size: 12px;
		line-height: 12px;
		margin-left: variables.$spacing-xs;
		font-family: variables.$font-family-telenor-base;
	}

	&.m .label {
		position: relative;
		top: 1px;
	}
}

@keyframes skeleton-loading {
	0% {
		// transform: translateX(-120%);
		background-color: rgb(0 0 0 / 20%);
	}

	100% {
		background-color: rgb(0 0 0 / 5%);

		// transform: translateX(100%);
	}
}
</style>
